import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthState } from 'modules/Auth/AuthContext';
import { Button, IconButton } from 'components/Button';
import { IconArrowDown, IconArrowTop, IconGripVertical, IconSettingsTwo } from 'components/Icons';
import { Popover, PopoverAction } from 'components/Popover';
import { Switch } from 'components/Switch';
import { useCompositePopover } from 'hooks/useCompositePopover';
import { useModalActions } from 'components/ModalManager/Context';
import { noop } from 'utils/fp';
import { notificationModalStyles, notificationModalWrapperStyles } from 'components/Navbar/Navbar.styles';
import { hideOnMobile, showOnMobile } from 'modules/Apps/components/CreateApplication/CreateApplicationModal.styles';
import { cx } from '@linaria/core';
import { flex } from 'styles/utils';
import { homeCardTitle, homeCardWrapper } from './HomeCard.styles';
import { configureHomeList, configureHomeListItem, configureHomeModal, configureHomeTitle, dragHandle } from '../HomePageLayout.styles';
import { useConfigureHome, ConfigureHomeCardType, ConfigureHomeContextType } from '../context';
import { HomeCardIds, mapIdToTitle } from '../constants';
import { useHomePageLayoutData } from '../hooks/useHomePageLayoutData';

const CONFIGURE_HOME_MODAL_ID = 'configure_home_modal_id';

function ConfigureHome(props: Pick<ConfigureHomeContextType, 'moveCardTo' | 'moveCardUp' | 'moveCardDown' | 'setCardVisibility'>) {
  // const { cards, moveCardUp, moveCardTo, moveCardDown, setCardVisibility } = useConfigureHome();
  const { cards } = useHomePageLayoutData();
  const { moveCardUp, moveCardTo, moveCardDown, setCardVisibility } = props;

  const [selectedCard, setSelectedCard] = useState<ConfigureHomeCardType | null>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [offsetPosition, setOffsetPosition] = useState({ x: 0, y: 0 });
  const [moveDir, setMoveDir] = useState(0);

  const ulRef = useRef(null);
  useEffect(() => {
    const handler = () => setSelectedCard(null);
    if (!window) return;
    window.addEventListener('mouseup', handler);
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('mouseup', handler);
    };
  }, []);

  const prevCard = useRef<ConfigureHomeCardType | null>(null);
  useEffect(() => {
    if (prevCard.current === null || selectedCard !== null) return;
    const currentCard = { ...(prevCard.current as ConfigureHomeCardType) };
    prevCard.current = null;
    moveCardTo(currentCard.id, Math.floor(position.y > 0 ? (position.y + 21) / 42 : 0));
  }, [selectedCard, position, moveCardTo]);

  const startDragging = useCallback(() => {
    const onPointerMove = (pointerMoveEvent: PointerEvent) => {
      if (!ulRef.current) return;
      const rect = (ulRef.current as HTMLUListElement).getBoundingClientRect();

      setPosition(() => {
        const newY = pointerMoveEvent.clientY - rect.y - offsetPosition.y;
        return {
          x: pointerMoveEvent.clientX - rect.x - offsetPosition.x,
          y: newY < 0 ? -1 : newY > rect.height - 42 ? rect.height - 41 : newY,
        };
      });
    };

    const onPointerUp = () => {
      setSelectedCard(null);
      window.removeEventListener('pointermove', onPointerMove);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerUp, {
      once: true,
    });
  }, [offsetPosition]);

  const registerStartMove = useCallback(
    (e: React.MouseEvent, card: ConfigureHomeCardType) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      let el = target;
      while (el.tagName !== 'LI') {
        el = el.parentElement as HTMLElement;
      }
      const rect = (el as HTMLElement).getBoundingClientRect();

      setOffsetPosition(() => ({
        x: e.clientX - rect.x,
        y: e.clientY - rect.y,
      }));
      prevCard.current = card;
      setSelectedCard(card);
    },
    [setOffsetPosition, setSelectedCard],
  );

  return (
    <>
      <h4 className={configureHomeTitle}>Configure Home</h4>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <ul
        ref={ulRef}
        className={configureHomeList}
        style={{
          height: cards.length * 42,
          boxSizing: 'border-box',
          userSelect: 'none',
          touchAction: 'none',
        }}
        onMouseMove={(e) => {
          setMoveDir(e.movementY > 0 ? -1 : 1);
          if (!ulRef.current) return;
          const rect = (ulRef.current as HTMLUListElement).getBoundingClientRect();
          setPosition(() => {
            const newY = e.clientY - rect.y - offsetPosition.y;
            return {
              x: e.clientX - rect.x - offsetPosition.x,
              y: newY < 0 ? -1 : newY > rect.height - 42 ? rect.height - 41 : newY,
            };
          });
        }}
        onMouseUp={() => {
          setSelectedCard(null);
        }}
      >
        <li
          className={configureHomeListItem}
          style={{
            backgroundColor: '#7777AA',
            borderRadius: '5px',
            opacity: selectedCard ? 0.05 : 0,
            transition: 'opacity 0.5s',
            top: 42 * Math.floor(position.y > 0 ? (position.y + 21) / 42 : 0),
          }}
        />

        {cards.map((card) => {
          return (
            <li
              data-key={`configure-home-${card.id}`}
              key={`configure-home-${card.id}`}
              className={configureHomeListItem}
              style={{
                boxSizing: 'border-box',
                zIndex: selectedCard && card.id === selectedCard.id ? 200 : undefined,
                rotate: selectedCard && card.id === selectedCard.id ? `${2 * moveDir}deg` : '',
                backgroundColor: selectedCard && card.id === selectedCard.id ? '#ffffffb9' : '',
                userSelect: 'none',
                transition: selectedCard && card.id === selectedCard.id ? 'rotate 0.2s' : selectedCard ? 'top rotate 0.3s' : 'all 0.3s',

                // left: card.id === selectedId ? position.x : 0,
                top:
                  selectedCard && card.id === selectedCard.id
                    ? position.y
                    : selectedCard && card.configIdx > selectedCard.configIdx && card.configIdx * 42 < position.y + 21
                    ? (card.configIdx - 1) * 42
                    : selectedCard && card.configIdx < selectedCard.configIdx && card.configIdx * 42 > position.y - 21
                    ? (card.configIdx + 1) * 42
                    : card.configIdx * 42,
              }}
            >
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div
                className={dragHandle}
                onPointerDown={(e) => {
                  registerStartMove(e, card);
                  startDragging();
                }}
                onMouseDown={(e) => {
                  registerStartMove(e, card);
                }}
                onMouseUp={() => {
                  setSelectedCard(null);
                }}
              >
                <IconGripVertical />
              </div>
              <span className="title">{mapIdToTitle[card.id]}</span>
              <IconButton
                iconSize={12}
                variant="tertiary"
                aria-label="Move Up"
                //  tooltip="Move Up"
                Icon={IconArrowTop}
                disabled={card.configIdx === 0}
                onClick={() => moveCardUp(card.id)}
              />
              <IconButton
                iconSize={12}
                variant="tertiary"
                aria-label="Move Down"
                // tooltip="Move Down"
                Icon={IconArrowDown}
                disabled={card.configIdx === HomeCardIds.length - 1}
                onClick={() => moveCardDown(card.id)}
              />
              <Switch
                aria-label="Toggle Visibility"
                isActive={card.visible}
                onChange={(e) => setCardVisibility(card.id, e.currentTarget.checked)}
                className="switch"
              />
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function HomePageHeader() {
  const { authData } = useAuthState();
  const { openModal, closeModal } = useModalActions();
  const { moveCardDown, moveCardTo, moveCardUp, setCardVisibility } = useConfigureHome();

  const closeConfigureHomeModal = () => closeModal({ id: CONFIGURE_HOME_MODAL_ID });

  const openConfigureHomeModal = useCallback(
    () =>
      openModal({
        id: CONFIGURE_HOME_MODAL_ID,
        title: '',
        makeActions: noop,
        contentWrapperClassName: cx(notificationModalStyles, configureHomeModal),
        className: notificationModalWrapperStyles,
        closeOnBackdropClick: true,
        content: <ConfigureHome moveCardTo={moveCardTo} moveCardDown={moveCardDown} moveCardUp={moveCardUp} setCardVisibility={setCardVisibility} />,
      }),
    [openModal],
  );

  const { popoverState, togglePopoverOrModal } = useCompositePopover({
    breakpointWidth: 800,
    modal: {
      id: CONFIGURE_HOME_MODAL_ID,
      open: openConfigureHomeModal,
      close: closeConfigureHomeModal,
    },
    popover: {
      placement: 'bottom-end',
    },
  });

  if (!authData) {
    return null;
  }

  return (
    <header className={homeCardWrapper}>
      <div className={cx(flex.init, flex.alignItems.center, flex.justifyContent.between, flex.gap.half)}>
        <h1 className={homeCardTitle}>👋 Welcome, {authData?.first_name}!</h1>

        <PopoverAction popover={popoverState}>
          <>
            <Button size="md" variant="tertiary" LeftIcon={IconSettingsTwo} onClick={togglePopoverOrModal} className={hideOnMobile}>
              Configure Home
            </Button>
            <IconButton
              size="md"
              variant="tertiary"
              aria-label="Configure Home"
              Icon={IconSettingsTwo}
              onClick={togglePopoverOrModal}
              className={showOnMobile}
            />
          </>
        </PopoverAction>

        <Popover
          popover={popoverState}
          style={{
            position: 'fixed',
            zIndex: 2,
            padding: '0.75rem 0.5rem',
          }}
        >
          <ConfigureHome moveCardTo={moveCardTo} moveCardDown={moveCardDown} moveCardUp={moveCardUp} setCardVisibility={setCardVisibility} />
        </Popover>
      </div>

      {/* <Plan /> */}
    </header>
  );
}
